import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const cognitoConfig = {
  clientId: process.env.REACT_APP_AWS_COGNITO_CLIENT_ID,
  domain: process.env.REACT_APP_AWS_COGNITO_MYRIAM_DOMAIN,
  redirectUri:
    process.env.REACT_APP_AWS_COGNITO_REDIRECT_URI ||
    `${window.location.origin}`,
  identityProvider: process.env.REACT_APP_AWS_COGNITO_MYRIAM_IDP,
  userPoolId: process.env.REACT_APP_AWS_COGNITO_USER_POOL_ID,
  region: process.env.REACT_APP_AWS_COGNITO_REGION,
  idpInitiated: process.env.REACT_APP_SAML_IDP_INITIATED === 'true',
  idpUrl: process.env.REACT_APP_SAML_IDP_URL,
  spId: process.env.REACT_APP_SAML_SP_ID,
};

export const useCognitoSaml = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error parsing JWT:', e);
      return null;
    }
  };

  const loginWithMYRIAM = useCallback(() => {
    // Check if IdP-initiated flow should be used (direct redirect to IdP with SPID)
    if (cognitoConfig.idpInitiated && cognitoConfig.idpUrl && cognitoConfig.spId) {
      console.log('IdP-initiated flow should be used');
      // Build the IdP URL with SPID parameter for IdP-initiated flow
      const url = new URL(cognitoConfig.idpUrl);
      url.searchParams.set('SPID', cognitoConfig.spId);

      // Store the current location for post-login redirect
      const returnUrl = window.location.hash.replace('#', '') || '/';
      console.log('returnUrl', returnUrl);
      sessionStorage.setItem('saml_return_url', returnUrl);

      window.location.href = url.toString();
      return;
    }

    const loginUrl =
      `https://${cognitoConfig.domain}/oauth2/authorize?` +
      `identity_provider=${cognitoConfig.identityProvider}&` +
      `redirect_uri=${encodeURIComponent(cognitoConfig.redirectUri)}&` +
      `response_type=CODE&` +
      `client_id=${cognitoConfig.clientId}&` +
      `scope=openid%20email%20profile`;

    window.location.href = loginUrl;
  }, []);

  const exchangeCodeForTokens = useCallback(async (code) => {
    try {
      const tokenUrl = `https://${cognitoConfig.domain}/oauth2/token`;

      const params = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: cognitoConfig.clientId,
        code,
        redirect_uri: cognitoConfig.redirectUri,
      });

      const response = await axios.post(tokenUrl, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      return response.data;
    } catch (err) {
      console.error('Error intercambiando código:', err);
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('cognito_id_token');
    localStorage.removeItem('cognito_access_token');
    localStorage.removeItem('cognito_refresh_token');
    localStorage.removeItem('cognito_user');
    setUser(null);

    const logoutUrl =
      `https://${cognitoConfig.domain}/logout?` +
      `client_id=${cognitoConfig.clientId}&` +
      `logout_uri=${encodeURIComponent(window.location.origin)}`;

    window.location.href = logoutUrl;
  }, []);

  const handleCallback = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const errorParam = urlParams.get('error');

      if (errorParam) {
        throw new Error(errorParam);
      }

      if (!code) {
        throw new Error('No se recibió código de autorización');
      }

      const tokens = await exchangeCodeForTokens(code);

      const payload = parseJwt(tokens.id_token);

      if (!payload) {
        throw new Error('Error al decodificar token');
      }

      const userData = {
        email: payload.email,
        name: payload.name,
        givenName: payload.given_name,
        familyName: payload.family_name,
        locale: payload.locale,
        sub: payload.sub,
      };

      localStorage.setItem('cognito_id_token', tokens.id_token);
      localStorage.setItem('cognito_access_token', tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem('cognito_refresh_token', tokens.refresh_token);
      }
      localStorage.setItem('cognito_user', JSON.stringify(userData));

      setUser(userData);
      setLoading(false);

      return { tokens, user: userData };
    } catch (err) {
      console.error('Error en callback:', err);
      setError(err.message || 'Error al procesar autenticación');
      setLoading(false);
      throw err;
    }
  }, [exchangeCodeForTokens]);

  const getCurrentUser = useCallback(() => {
    try {
      const storedUser = localStorage.getItem('cognito_user');
      const idToken = localStorage.getItem('cognito_id_token');

      if (storedUser && idToken) {
        const userData = JSON.parse(storedUser);

        const payload = parseJwt(idToken);
        if (payload && payload.exp * 1000 > Date.now()) {
          setUser(userData);
          return userData;
        }
        logout();
        return null;
      }
      return null;
    } catch (err) {
      console.error('Error al obtener usuario:', err);
      return null;
    }
  }, []);

  const isAuthenticated = useCallback(() => {
    const idToken = localStorage.getItem('cognito_id_token');
    if (!idToken) return false;

    const payload = parseJwt(idToken);
    return payload && payload.exp * 1000 > Date.now();
  }, []);

  const getIdToken = useCallback(
    () => localStorage.getItem('cognito_id_token'),
    []
  );

  const getAccessToken = useCallback(
    () => localStorage.getItem('cognito_access_token'),
    []
  );

  useEffect(() => {
    getCurrentUser();
  }, [getCurrentUser]);

  return {
    user,
    loading,
    error,
    loginWithMYRIAM,
    handleCallback,
    getCurrentUser,
    isAuthenticated,
    getIdToken,
    getAccessToken,
    logout,
  };
};
